import type { FileWithId } from 'src/types';
import type { BucketParamsObject } from 'src/types/s3';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import { uuidv4 } from 'minimal-shared/utils';
import { memo, useRef, useMemo, useEffect, useCallback, type MutableRefObject } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import InputAdornment from '@mui/material/InputAdornment';
import TablePagination from '@mui/material/TablePagination';

import { AUDIO_FILE_FORMATS } from 'src/utils/constants';

import { Iconify } from 'src/components/iconify';
import { EmptyContent } from 'src/components/empty-content';

import { getColumns } from './columns';
import ResponseLoader from '../../ChatResponseLoader';
import { useMessageContext } from '../../hooks/uesMessageContext';
import { useSettingsContext } from '../../hooks/useSettingsContext';
import { useChatHistoryContext } from '../../hooks/useChatHistoryContext';

type Breadcrumb = {
  label: string;
  path: string;
};

type RightSideProps = {
  selectedFilesRef: MutableRefObject<GridRowSelectionModel>;
  selectedBucket: string | null;
  bucketDetails: any;
  isLoadingBucketDetails: boolean;
  bucketParams: BucketParamsObject;
  setBucketParams: (
    params: BucketParamsObject | ((prev: BucketParamsObject) => BucketParamsObject)
  ) => void;
  selectedRowsData: FileWithId[];
  setSelectedRowsData: (data: FileWithId[]) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  currentPath: string;
  setCurrentPath: (path: string) => void;
  open: boolean;
};

// SearchBar Component
const SearchBar = memo(
  ({
    searchTerm,
    setSearchTerm,
    selectedFilesCount,
  }: {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    selectedFilesCount: number;
  }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <TextField
        sx={{ width: 300 }}
        size="small"
        placeholder="Search file name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          },
        }}
      />
      {selectedFilesCount > 0 && (
        <Box>
          <Typography variant="subtitle1">{selectedFilesCount} files selected</Typography>
        </Box>
      )}
    </Stack>
  )
);

// Breadcrumbs Component
const FileBreadcrumbs = memo(
  ({
    selectedBucket,
    currentPath,
    breadcrumbs,
    setCurrentPath,
    setBucketParams,
    handleBreadcrumbClick,
  }: {
    selectedBucket: string | null;
    currentPath: string;
    breadcrumbs: Breadcrumb[];
    setCurrentPath: (path: string) => void;
    setBucketParams: (
      params: BucketParamsObject | ((prev: BucketParamsObject) => BucketParamsObject)
    ) => void;
    handleBreadcrumbClick: (path: string) => void;
  }) => (
    <Breadcrumbs aria-label="breadcrumb">
      <Link
        component="button"
        variant="body2"
        onClick={() => {
          setBucketParams({
            page: 1,
            page_size: 10,
            prefix: null,
          });
          setCurrentPath('');
        }}
        sx={{ textDecoration: 'none' }}
      >
        {selectedBucket}
      </Link>
      {currentPath &&
        breadcrumbs.map((crumb: Breadcrumb) => (
          <Link
            key={crumb.path}
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick(crumb.path)}
            sx={{ textDecoration: 'none' }}
          >
            {crumb.label}
          </Link>
        ))}
    </Breadcrumbs>
  )
);

// DataGrid Component
const FileDataGrid = memo(
  ({
    filteredBucketDetails,
    columns,
    handleSelectionChange,
    selectedFilesRef,
    isRowSelectable,
    isLoadingBucketDetails,
    selectedBucket,
  }: {
    filteredBucketDetails: any;
    columns: any[];
    handleSelectionChange: (newSelection: GridRowSelectionModel) => void;
    selectedFilesRef: MutableRefObject<GridRowSelectionModel>;
    isRowSelectable: (params: any) => boolean;
    isLoadingBucketDetails: boolean;
    selectedBucket: string | null;
  }) => (
    <DataGrid
      checkboxSelection
      disableRowSelectionOnClick
      keepNonExistentRowsSelected
      hideFooter
      rows={filteredBucketDetails?.objects || []}
      columns={columns}
      onRowSelectionModelChange={handleSelectionChange}
      rowSelectionModel={selectedFilesRef.current}
      isRowSelectable={isRowSelectable}
      loading={isLoadingBucketDetails}
      slots={{
        noRowsOverlay: () => (
          <EmptyContent title={selectedBucket ? 'No data' : 'Select a bucket to view files'} />
        ),
        noResultsOverlay: () => <EmptyContent title="No results found" />,
        loadingOverlay: () => (
          <Typography component="div" sx={{ marginTop: 2, textAlign: 'center' }}>
            <ResponseLoader />
          </Typography>
        ),
      }}
      sx={{
        [`& .${gridClasses.cell}`]: {
          alignItems: 'center',
          display: 'inline-flex',
        },
      }}
    />
  )
);

// Pagination Component
const FilePagination = memo(
  ({
    bucketDetails,
    bucketParams,
    setBucketParams,
  }: {
    bucketDetails: any;
    bucketParams: BucketParamsObject;
    setBucketParams: (
      params: BucketParamsObject | ((prev: BucketParamsObject) => BucketParamsObject)
    ) => void;
  }) => (
    <Box
      sx={{
        width: '100%',
        height: '10.5%',
        borderTop: '1px dashed #e0e0e0',
        alignSelf: 'center',
      }}
    >
      <TablePagination
        component="div"
        count={bucketDetails?.pagination?.totalItems || 0}
        page={Math.min(
          bucketParams.page - 1,
          Math.max(
            0,
            Math.ceil((bucketDetails?.pagination?.totalItems || 0) / bucketParams.page_size) - 1
          )
        )}
        rowsPerPage={bucketParams.page_size}
        onPageChange={(_, newPage) => {
          setBucketParams((prev) => ({ ...prev, page: newPage + 1 }));
        }}
        onRowsPerPageChange={(event) => {
          const newSize = parseInt(event.target.value, 10);
          setBucketParams((prev) => ({ ...prev, page_size: newSize, page: 1 }));
        }}
        rowsPerPageOptions={[]}
      />
    </Box>
  )
);

export const RightSide = memo(
  ({
    selectedFilesRef,
    selectedBucket,
    bucketDetails,
    isLoadingBucketDetails,
    bucketParams,
    setBucketParams,
    selectedRowsData,
    setSelectedRowsData,
    searchTerm,
    setSearchTerm,
    currentPath,
    setCurrentPath,
    open,
  }: RightSideProps) => {
    const { allFiles, currentFileFormat, setCurrentFileFormat } = useChatHistoryContext();
    const { getCurrentModel } = useSettingsContext();
    const { message } = useMessageContext();
    const currentFormat = useRef(currentFileFormat);

    const currentModelFileSupport = useMemo(
      () =>
        getCurrentModel()?.fileTypeSupport.map((each: { label: string }) =>
          each.label.replace('.', '').toLowerCase()
        ),
      [getCurrentModel]
    );

    const allFilesS3Length = useMemo(
      () => allFiles?.filter((file) => !file.isS3Upload).length || 0,
      [allFiles]
    );

    // Filter bucketDetails objects based on search term (case insensitive)
    const filteredBucketDetails = useMemo(() => {
      if (!bucketDetails || !bucketDetails.objects) return { objects: [] };

      if (!searchTerm) return bucketDetails;

      const filteredObjects = bucketDetails.objects.filter((obj: any) => {
        const fileName = obj.key.split('/').pop() || obj.key;
        return fileName.toLowerCase().includes(searchTerm.toLowerCase());
      });

      return {
        ...bucketDetails,
        objects: filteredObjects,
      };
    }, [bucketDetails, searchTerm]);

    // Memoize breadcrumbs calculation
    const breadcrumbs = useMemo(() => {
      if (!currentPath) return [];
      const parts = currentPath.split('/').filter(Boolean);
      return parts.map((part, index) => {
        const path = parts.slice(0, index + 1).join('/');
        return {
          label: part,
          path,
        };
      });
    }, [currentPath]);

    const handleSelectionChange = useCallback(
      (newSelection: GridRowSelectionModel) => {
        selectedFilesRef.current = newSelection;
        const newSelectionSet = new Set(newSelection);
        const currentSelectedRows =
          bucketDetails?.objects
            ?.filter((row: any) => newSelectionSet.has(row.id) && !row.isFolder)
            ?.map((row: any) => {
              const fileExtension = row.key.split('.').pop() || row.key;
              const fileName = row.key.split('/').pop() || row.key;
              return {
                id: row.id,
                uuid: uuidv4(), // this is to use for chat_with_audio_s3
                file: {
                  name: fileName,
                  size: row.size,
                  type: fileExtension,
                },
                currentPath: `${selectedBucket}/${row.id}`,
                isS3Upload: true,
              };
            }) || [];

        const updatedSelections = [
          ...selectedRowsData.filter(
            (item) => !bucketDetails?.objects?.some((row: any) => row.id === item.id)
          ),
          ...currentSelectedRows,
        ];

        const uniqueSelections = Array.from(
          new Map(updatedSelections.map((item) => [item.id, item])).values()
        );
        setSelectedRowsData(uniqueSelections);

        // Handle setCurrentFileFormat logic
        const selectedFile = newSelection.filter(
          (selection) => !allFiles.some((file) => file.id === selection)
        );
        if (!currentFileFormat) {
          // When first file is selected, set its format
          if (selectedFile) {
            const fileName = selectedFile[0].toString().split('/').pop() || '';
            const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
            setCurrentFileFormat(`.${fileExtension}`);
            currentFormat.current = `.${fileExtension}`;
          }
        } else if (
          newSelection.length === 0 &&
          message.files.filter((file) => !file.isS3Upload).length === 0
        ) {
          // When all files are deselected and no other files exist, clear the format
          setCurrentFileFormat(undefined);
          currentFormat.current = undefined;
        } else if (
          selectedFile.length === 0 &&
          message.files.filter((file) => !file.isS3Upload).length === 0
        ) {
          setCurrentFileFormat(undefined);
          currentFormat.current = undefined;
        }
      },
      [
        bucketDetails?.objects,
        selectedBucket,
        selectedRowsData,
        setSelectedRowsData,
        selectedFilesRef,
        setCurrentFileFormat,
        currentFileFormat,
        message,
        allFiles,
      ]
    );

    const handleBreadcrumbClick = useCallback(
      (path: string) => {
        setBucketParams((prev) => ({
          ...prev,
          prefix: `${path}/`,
        }));
        setCurrentPath(path);
      },
      [setBucketParams, setCurrentPath]
    );

    const handleFolderClick = useCallback(
      (_prefix: string) => {
        setBucketParams({
          page: 1,
          page_size: 10,
          prefix: _prefix,
        });
      },
      [setBucketParams]
    );

    const isRowSelectable = useCallback(
      (params: any) => {
        if (params.row.isFolder) {
          return false;
        }
        if (AUDIO_FILE_FORMATS.includes(currentFormat?.current || '')) {
          return selectedFilesRef.current.includes(params.row.id);
        }
        const fileKey = params.row.key || '';
        const fileName = fileKey.split('/').pop() || fileKey;
        const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
        const isFileTypeSupported = !currentFormat.current
          ? currentModelFileSupport.includes(fileExtension)
          : currentFormat.current == `.${fileExtension}`;

        return (
          (!params.row.isFolder &&
            isFileTypeSupported &&
            selectedFilesRef.current.length < 5 - allFilesS3Length) ||
          selectedFilesRef.current.includes(params.row.id)
        );
      },
      [selectedFilesRef, currentModelFileSupport, allFilesS3Length]
    );

    const columns = useMemo(
      () => getColumns(setCurrentPath, handleFolderClick),
      [setCurrentPath, handleFolderClick]
    );

    useEffect(() => {
      setSelectedRowsData(allFiles?.filter((file) => file.isS3Upload));
      selectedFilesRef.current =
        allFiles?.filter((file) => file.isS3Upload)?.map((file) => file.id) || [];
    }, [open, allFiles, setSelectedRowsData, selectedFilesRef]);

    return (
      <Box
        sx={{
          gap: 2,
          width: 1,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          mb: 2,
          height: '100%',
        }}
      >
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedFilesCount={selectedFilesRef.current.length}
        />

        <FileBreadcrumbs
          selectedBucket={selectedBucket}
          currentPath={currentPath}
          breadcrumbs={breadcrumbs}
          setCurrentPath={setCurrentPath}
          setBucketParams={setBucketParams}
          handleBreadcrumbClick={handleBreadcrumbClick}
        />

        <Box
          sx={{
            minHeight: 420,
            width: '100%',
            flexGrow: { md: 1 },
            display: { md: 'flex' },
            height: { xs: 800, md: '1px' },
            flexDirection: { md: 'column' },
          }}
        >
          <FileDataGrid
            filteredBucketDetails={filteredBucketDetails}
            columns={columns}
            handleSelectionChange={handleSelectionChange}
            selectedFilesRef={selectedFilesRef}
            isRowSelectable={isRowSelectable}
            isLoadingBucketDetails={isLoadingBucketDetails}
            selectedBucket={selectedBucket}
          />
        </Box>

        <FilePagination
          bucketDetails={bucketDetails}
          bucketParams={bucketParams}
          setBucketParams={setBucketParams}
        />
      </Box>
    );
  }
);
